import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import {
  ButtonType,
  IconPaths,
  IconSize,
  KTSVG,
  formatDateTime,
  showError,
} from '../../../../_metronic/helpers'
import { Button, ContactSupportLink, LoadingIndicator } from '../../../components'
import { TermoConsentimentoPublic } from '../core/_models'
import { getTermoByProtocolo, registrarAceiteTermo } from '../core/_requests'
import { getTeleAuth } from '../core/teleConsultaAuth'
import styles from './styles/ConsentimentoTeleConsulta.module.css'

/**
 * Etapa 2 do acesso do paciente à teleconsulta: exibe o termo de consentimento vigente do
 * atendimento e registra o aceite. Só depois do aceite o paciente entra no atendimento — o
 * backend barra o acesso com 403 `ACEITE_TERMO_PENDENTE` enquanto o aceite não existir.
 */
export const ConsentimentoTeleConsulta = () => {
  const { protocolo } = useParams()
  const navigate = useNavigate()

  const [termo, setTermo] = useState<TermoConsentimentoPublic>()
  const [isLoading, setIsLoading] = useState(true)
  const [erroCarga, setErroCarga] = useState<string>()
  const [aceiteTermo, setAceiteTermo] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const tituloRef = useRef<HTMLHeadingElement>(null)
  const temToken = Boolean(getTeleAuth()?.accessToken)

  useEffect(() => {
    if (!temToken) navigate(`/tele-consulta/${protocolo}`)
  }, [navigate, temToken, protocolo])

  const carregarTermo = useCallback(async () => {
    // Sem token não há o que buscar: o efeito acima já devolve o paciente para a etapa 1.
    if (!protocolo || !temToken) return
    setIsLoading(true)
    setErroCarga(undefined)
    try {
      setTermo(await getTermoByProtocolo(protocolo))
    } catch (error) {
      setErroCarga(
        'Não foi possível carregar o Termo de Consentimento. Verifique sua conexão e tente novamente.'
      )
    } finally {
      setIsLoading(false)
    }
  }, [protocolo, temToken])

  useEffect(() => {
    carregarTermo()
  }, [carregarTermo])

  // Foco no título assim que a etapa fica pronta: a transição vem de outra tela, então o leitor
  // de tela precisa ser levado ao início do novo conteúdo.
  useEffect(() => {
    if (!isLoading) tituloRef.current?.focus()
  }, [isLoading])

  const confirmarAceite = async () => {
    if (!protocolo || !termo || !aceiteTermo) return
    setIsSubmitting(true)
    try {
      await registrarAceiteTermo({ idTermo: termo.id, protocolo })
      navigate(`/tele-consulta/${protocolo}/atendimento`)
    } catch (error) {
      // Erro recuperável: permanece na etapa, mantendo o termo carregado e o aceite marcado.
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='container d-flex flex-column justify-content-center align-items-center flex-grow-1 py-5'>
      <div
        className='d-flex flex-column bg-gray-200 rounded-4 w-100 p-4 p-md-5'
        style={{ maxWidth: '620px' }}
      >
        <div className={styles.badgeWrap} aria-hidden='true'>
          <span className={styles.badge}>
            <KTSVG path={IconPaths.Termo} size={IconSize.Medium} />
          </span>
        </div>

        <div className='text-center mb-6'>
          <p className={`${styles.etapa} text-primary fw-bold fs-7 mb-2`}>
            Etapa 2 de 2 — Consentimento
          </p>
          <h1 className='text-dark mb-2 fs-2x' ref={tituloRef} tabIndex={-1}>
            {termo?.titulo ?? 'Termo de Consentimento'}
          </h1>
          {termo && (
            <p className='text-gray-700 fs-7 mb-0'>
              Versão {termo.versao}
              {termo.dtVigenciaInicio &&
                ` — vigente desde ${formatDateTime(termo.dtVigenciaInicio)}`}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className='d-flex justify-content-center py-10'>
            <LoadingIndicator />
          </div>
        ) : erroCarga ? (
          <div className='text-center'>
            <p className='text-danger fs-6' role='alert'>
              {erroCarga}
            </p>
            <Button
              type={ButtonType.Button}
              className='btn-primary'
              onClick={carregarTermo}
              icon={IconPaths.Refresh}
              iconSize={IconSize.Small}
            >
              Tentar novamente
            </Button>
          </div>
        ) : (
          termo && (
            <>
              <div
                className={`${styles.conteudoTermo} border rounded p-4 mb-6 bg-body`}
                role='region'
                aria-label='Texto do Termo de Consentimento'
                tabIndex={0}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termo.conteudo) }}
              />

              <div className='d-flex align-items-center mb-2'>
                <input
                  type='checkbox'
                  className={`form-check-input me-3 flex-shrink-0 ${styles.aceite}`}
                  id='aceiteTermo'
                  name='aceiteTermo'
                  checked={aceiteTermo}
                  onChange={(e) => setAceiteTermo(e.target.checked)}
                  style={{ width: '1.75rem', height: '1.75rem' }}
                />
                <label className='fs-6 text-gray-800' htmlFor='aceiteTermo'>
                  Li e aceito o Termo de Consentimento
                </label>
              </div>

              <Button
                type={ButtonType.Button}
                className='btn-primary btn-auth-full mt-6'
                onClick={confirmarAceite}
                disabled={!aceiteTermo || isSubmitting}
                icon={isSubmitting ? undefined : IconPaths.Check}
                iconSize={IconSize.Small}
              >
                {isSubmitting ? <LoadingIndicator /> : 'Confirmar e entrar no atendimento'}
              </Button>
            </>
          )
        )}

        <div className='text-center mt-4'>
          <ContactSupportLink message='Dúvidas sobre o termo? Fale com o suporte.' />
        </div>
      </div>
    </div>
  )
}
